import { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import { dashboardApi, magazzinoApi2, kitApi, whatsappApi } from '../services/api'
import { useIsMobile } from '../hooks/useIsMobile'
import { DashboardKPI, StoricoProduzioneItem, MaterialiTecnico, PerformanceTecnico } from '../types'
import {
  ClipboardList, Clock, CheckCircle2,
  FileText, Receipt, TrendingUp, ArrowUpRight, PackageCheck,
  Users, CalendarDays, BarChart2, Zap, MessageCircle, Printer,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Area, AreaChart,
} from 'recharts'
import html2canvas from 'html2canvas'
import toast from 'react-hot-toast'

const WHATSAPP_TEST_NUMBER = '393382609180'
const KPI_SCREENSHOT_TILE_COUNT = 9

const MESI   = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic']
const COLORS = ['#1E40AF','#F97316','#10b981','#ef4444','#8b5cf6','#f59e0b','#06b6d4','#ec4899']
const AVATAR_COLORS = [
  { bg: '#E6F1FB', color: '#0C447C' },
  { bg: '#ede9fe', color: '#5b21b6' },
  { bg: '#d1fae5', color: '#065f46' },
  { bg: '#fef3c7', color: '#92400e' },
  { bg: '#e0f2fe', color: '#0369a1' },
  { bg: '#f0fdf4', color: '#166534' },
]

/* ── KPI Card ─────────────────────────────────────────── */
interface KpiCardProps {
  title: string
  value: number | string
  icon: React.ElementType
  accent: string
  subtitle?: string
  to?: string
  animDelay?: number
}

function KpiCard({ title, value, icon: Icon, accent, subtitle, to, animDelay = 0 }: KpiCardProps) {
  const card = (
    <div
      data-kpi-tile
      className="animate-fade-up relative rounded-xl p-4 overflow-hidden group transition-all duration-200"
      style={{
        background: '#ffffff', border: '1px solid #e5e7eb',
        borderTop: `3px solid ${accent}`, animationDelay: `${animDelay}ms`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = `0 4px 16px rgba(0,0,0,0.10), 0 0 0 1px ${accent}30`
        e.currentTarget.style.transform = 'translateY(-2px)'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)'
        e.currentTarget.style.transform = 'translateY(0)'
      }}
    >
      <div className="absolute top-0 right-0 w-20 h-20 pointer-events-none rounded-bl-full" style={{
        background: `radial-gradient(circle at top right, ${accent}12, transparent 70%)`,
      }} />
      <div className="relative flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#9ca3af', fontFamily: "'Figtree', sans-serif" }}>
            {title}
          </p>
          <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '2rem', fontWeight: 700, color: '#111827', letterSpacing: '-0.03em', marginTop: 8, lineHeight: 1 }}>
            {value}
          </p>
          {subtitle && <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: 6 }}>{subtitle}</p>}
        </div>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: `${accent}14`, color: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginLeft: 12 }}>
          <Icon size={17} />
        </div>
      </div>
      {to && (
        <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <ArrowUpRight size={13} style={{ color: accent }} />
        </div>
      )}
    </div>
  )
  if (to) return <Link to={to} className="block">{card}</Link>
  return card
}

interface StackedKpiRow { title: string; value: string; isTotale?: boolean }

function StackedKpiCard({ title, icon: Icon, accent, rows, animDelay = 0 }: { title: string; icon: React.ElementType; accent: string; rows: StackedKpiRow[]; animDelay?: number }) {
  return (
    <div
      data-kpi-tile
      className="animate-fade-up relative rounded-xl overflow-hidden transition-all duration-200"
      style={{
        background: '#ffffff', border: '1px solid #e5e7eb',
        borderTop: `3px solid ${accent}`, animationDelay: `${animDelay}ms`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
        padding: '0.9rem 1rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0, background: `${accent}14`, color: accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={14} />
        </div>
        <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#9ca3af', fontFamily: "'Figtree', sans-serif" }}>
          {title}
        </p>
      </div>
      {rows.map((r, i) => (
        <div
          key={r.title}
          style={{
            display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
            padding: '4px 0',
            borderTop: r.isTotale ? '1px solid #f3f4f6' : 'none',
            marginTop: r.isTotale ? 4 : 0,
          }}
        >
          <span style={{ fontSize: r.isTotale ? 12 : 11, fontWeight: r.isTotale ? 700 : 500, color: r.isTotale ? '#111827' : '#64748b' }}>
            {r.title}
          </span>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: r.isTotale ? '1.15rem' : '0.95rem', fontWeight: 700, color: r.isTotale ? accent : '#111827' }}>
            {r.value}
          </span>
        </div>
      ))}
    </div>
  )
}

function MediaRisorsaCard({ meseCorrente, storico, animDelay = 0 }: {
  meseCorrente: number
  storico: { anno: number; mese: number; media_eur_giorno_risorsa: number }[]
  animDelay?: number
}) {
  const accent = '#F97316'
  const storicoDesc = [...storico].reverse()
  return (
    <div
      data-kpi-tile
      className="animate-fade-up relative rounded-xl overflow-hidden transition-all duration-200"
      style={{
        background: '#ffffff', border: '1px solid #e5e7eb',
        borderTop: `3px solid ${accent}`, animationDelay: `${animDelay}ms`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
        padding: '0.9rem 1rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <div style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0, background: `${accent}14`, color: accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <TrendingUp size={14} />
        </div>
        <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#9ca3af', fontFamily: "'Figtree', sans-serif" }}>
          Media €/Giorno·Risorsa
        </p>
      </div>
      <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.6rem', fontWeight: 700, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1 }}>
        € {meseCorrente.toLocaleString('it-IT', { maximumFractionDigits: 0 })}
      </p>
      <p style={{ fontSize: 10, color: '#9ca3af', marginTop: 4, marginBottom: 6 }}>mese corrente — valore esatto per coppia tecnico+giorno</p>
      {storicoDesc.length > 0 && (
        <div style={{ maxHeight: 92, overflowY: 'auto', borderTop: '1px solid #f3f4f6', paddingTop: 4 }}>
          {storicoDesc.map(m => (
            <div key={`${m.anno}-${m.mese}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontSize: 11 }}>
              <span style={{ color: '#64748b' }}>{MESI[m.mese - 1]} {m.anno}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: '#111827' }}>
                € {m.media_eur_giorno_risorsa.toLocaleString('it-IT', { maximumFractionDigits: 0 })}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

interface DualKpiItem { title: string; value: number | string; icon: React.ElementType; color: string; subtitle?: string }

function DualKpiCard({ items, accent, animDelay = 0 }: { items: DualKpiItem[]; accent: string; animDelay?: number }) {
  return (
    <div
      className="animate-fade-up relative rounded-xl overflow-hidden transition-all duration-200 col-span-2 lg:col-span-1"
      style={{
        background: '#ffffff', border: '1px solid #e5e7eb',
        borderTop: `3px solid ${accent}`, animationDelay: `${animDelay}ms`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
        display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
      }}
    >
      {items.map((it, i) => (
        <div key={it.title} data-kpi-tile style={{ padding: '1rem', borderLeft: i > 0 ? '1px solid #f3f4f6' : 'none', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0, background: `${it.color}14`, color: it.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <it.icon size={15} />
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#9ca3af', fontFamily: "'Figtree', sans-serif" }}>
              {it.title}
            </p>
            <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.4rem', fontWeight: 700, color: it.color, letterSpacing: '-0.02em', marginTop: 4, lineHeight: 1 }}>
              {it.value}
            </p>
            {it.subtitle && <p style={{ fontSize: '10px', color: '#9ca3af', marginTop: 4 }}>{it.subtitle}</p>}
          </div>
        </div>
      ))}
    </div>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
      <span style={{ width: 3, height: 14, borderRadius: 99, background: '#1E40AF', display: 'inline-block' }} />
      <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6b7280', fontFamily: "'Figtree', sans-serif" }}>
        {children}
      </span>
    </div>
  )
}

function LightTooltip({ active, payload, label, formatter }: {
  active?: boolean; payload?: { name: string; value: number; color: string }[]; label?: string; formatter?: (v: number) => string
}) {
  if (!active || !payload?.length) return null
  return (
    <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '8px 12px', boxShadow: '0 4px 16px rgba(0,0,0,0.10)', fontSize: 12 }}>
      <p style={{ color: '#6b7280', fontWeight: 600, marginBottom: 6 }}>{label}</p>
      {payload.map(p => (
        <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
          <span style={{ color: '#111827', fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>
            {formatter ? formatter(p.value) : p.value}
          </span>
          <span style={{ color: '#9ca3af', fontSize: 11 }}>{p.name}</span>
        </div>
      ))}
    </div>
  )
}

function WoTecnicoBadge({ value }: { value: number }) {
  const [bg, color] = value >= 28 ? ['#d1fae5','#065f46'] : value >= 22 ? ['#fef3c7','#92400e'] : ['#fee2e2','#991b1b']
  return <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 99, background: bg, color, fontSize: 11, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{value.toFixed(1)}</span>
}

function TecnicoAvatar({ nome, cognome, colorIdx }: { nome: string; cognome: string; colorIdx: number }) {
  const c = AVATAR_COLORS[colorIdx % AVATAR_COLORS.length]
  return (
    <div style={{ width: 26, height: 26, borderRadius: '50%', background: c.bg, color: c.color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
      {`${nome[0] ?? ''}${cognome[0] ?? ''}`.toUpperCase()}
    </div>
  )
}

function PerfBadge({ tasso }: { tasso: number }) {
  const [label, bg, color] = tasso >= 90 ? ['Alta','#d1fae5','#065f46'] : tasso >= 70 ? ['Media','#fef3c7','#92400e'] : ['Bassa','#fee2e2','#991b1b']
  return <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 99, background: bg, color, fontSize: 11, fontWeight: 600 }}>{label}</span>
}

function ScortaBadge({ scorta }: { scorta: string }) {
  const map: Record<string, [string, string]> = {
    buona:   ['Buona',   '#d1fae5,#065f46'],
    bassa:   ['Bassa',   '#fef3c7,#92400e'],
    critica: ['Critica', '#fee2e2,#991b1b'],
    vuoto:   ['Vuoto',   '#f3f4f6,#6b7280'],
  }
  const parts = (map[scorta] ?? ['—', '#f3f4f6,#6b7280'])
  const label = parts[0]
  const [bg, color] = parts[1].split(',')
  return <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 99, background: bg, color, fontSize: 11, fontWeight: 600 }}>{label}</span>
}

/* ── Dashboard ───────────────────────────────────────── */
export default function Dashboard() {
  const { user } = useAuth()
  const isTecnico = ['tecnico', 'tecnico_senior'].includes(user?.role ?? '')
  const isStaff   = ['superadmin', 'admin', 'dispatcher', 'responsabile_tecnico'].includes(user?.role ?? '')
  const isMobile  = useIsMobile()

  const kpiScreenshotRef = useRef<HTMLDivElement>(null)
  const [sendingScreenshot, setSendingScreenshot] = useState(false)

  const inviaScreenshotWhatsapp = async () => {
    const container = kpiScreenshotRef.current
    if (!container) return
    setSendingScreenshot(true)
    try {
      await document.fonts.ready

      // Le card entrano con un'animazione CSS (animate-fade-up + animationDelay
      // scaglionato). Se lo screenshot scatta mentre è ancora in corso,
      // html2canvas cattura le card a metà transizione (semi-trasparenti /
      // spostate). Le "congeliamo" allo stato finale prima di catturare,
      // poi ripristiniamo l'animazione originale.
      const animatedEls = container.querySelectorAll<HTMLElement>('.animate-fade-up')
      const prevAnimation: string[] = []
      animatedEls.forEach((el, i) => {
        prevAnimation[i] = el.style.animation
        el.style.animation = 'none'
        el.style.opacity = '1'
        el.style.transform = 'none'
      })
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)))

      const tiles = container.querySelectorAll<HTMLElement>('[data-kpi-tile]')
      let canvas: HTMLCanvasElement
      try {
        canvas = await html2canvas(container, {
          backgroundColor: '#f0f2f5',
          scale: 2,
        })
      } finally {
        animatedEls.forEach((el, i) => {
          el.style.animation = prevAnimation[i]
          el.style.removeProperty('opacity')
          el.style.removeProperty('transform')
        })
      }
      const scale = canvas.width / container.getBoundingClientRect().width

      let cropHeight = canvas.height
      if (tiles.length >= KPI_SCREENSHOT_TILE_COUNT) {
        const lastTile = tiles[KPI_SCREENSHOT_TILE_COUNT - 1]
        const containerTop = container.getBoundingClientRect().top
        const tileBottom = lastTile.getBoundingClientRect().bottom
        // padding extra per non tagliare bordo/ombra dell'ultima card
        cropHeight = Math.min(canvas.height, Math.round((tileBottom - containerTop) * scale) + 24)
      }

      const cropped = document.createElement('canvas')
      cropped.width = canvas.width
      cropped.height = cropHeight
      const ctx = cropped.getContext('2d')
      if (!ctx) throw new Error('canvas context non disponibile')
      ctx.drawImage(canvas, 0, 0)

      const imageBase64 = cropped.toDataURL('image/png').split(',')[1]
      await whatsappApi.sendImage(WHATSAPP_TEST_NUMBER, imageBase64, 'Dashboard Operativa — screenshot KPI')
      toast.success('Screenshot inviato su WhatsApp')
    } catch (err) {
      console.error(err)
      toast.error('Invio screenshot WhatsApp fallito')
    } finally {
      setSendingScreenshot(false)
    }
  }

  const { data: kpi, isLoading, isError, refetch } = useQuery<DashboardKPI>({
    queryKey: ['dashboard-kpi'],
    queryFn: () => dashboardApi.kpi().then(r => r.data),
    refetchInterval: 60_000,
  })
  const { data: andamento } = useQuery({
    queryKey: ['andamento-mensile'],
    queryFn: () => dashboardApi.andamentoMensile().then(r => r.data),
    enabled: !isTecnico,
  })
  const { data: perRegione } = useQuery<{ regione: string; assegnato: number; ricevuto: number; totale: number }[]>({
    queryKey: ['per-regione'],
    queryFn: () => dashboardApi.perRegione().then(r => r.data),
    refetchInterval: 60_000,
    enabled: !isTecnico,
  })
  const { data: trasfData } = useQuery({
    queryKey: isTecnico ? ['trasf-in-arrivo-dash'] : ['trasf-verso-centrale-dash'],
    queryFn: isTecnico
      ? () => magazzinoApi2.trasferimentiInArrivo().then(r => r.data)
      : () => magazzinoApi2.trasferimentiVersoCentrale().then(r => r.data),
    enabled: isTecnico || isStaff,
    refetchInterval: 120_000,
  })
  const { data: kitMagazzinoData } = useQuery({
    queryKey: ['kit-magazzino-count'],
    queryFn: () => kitApi.list({ attivo: true }).then(r => r.data),
    enabled: !isTecnico,
    staleTime: 60_000,
  })

  const nowMonth = new Date().getMonth() + 1
  const { data: storicoProd } = useQuery<StoricoProduzioneItem[]>({
    queryKey: ['storico-produzione'],
    queryFn: () => dashboardApi.storicoProduzione().then(r => r.data),
    enabled: !isTecnico,
  })
  const { data: performanceTecnici } = useQuery<PerformanceTecnico[]>({
    queryKey: ['performance-tecnici', nowMonth],
    queryFn: () => dashboardApi.perTecnico({ mese: nowMonth }).then(r => r.data),
    refetchInterval: 120_000,
  })

  const { data: materialiTecnici, refetch: refetchMateriali } = useQuery<MaterialiTecnico[]>({
    queryKey: ['materiali-tecnici'],
    queryFn: () => dashboardApi.materialiTecnici().then(r => r.data),
    refetchInterval: 30_000,
  })

  const trasfCount: number = Array.isArray(trasfData) ? trasfData.length : 0
  const kitMagazzinoCount: number = Array.isArray(kitMagazzinoData) ? kitMagazzinoData.filter((k: any) => !k.stato || (k.stato !== 'su_furgone' && k.stato !== 'installato')).length : 0
  const importoMedioGiornoRisorsa = kpi?.produttivita?.media_eur_giorno_risorsa ?? 0
  const u = user as { nome?: string; cognome?: string; role?: string } | null
  const mioTecnico = performanceTecnici?.find(t => t.nome === u?.nome && t.cognome === u?.cognome)
  const miaMateriali = materialiTecnici?.find(t => t.nome === u?.nome && t.cognome === u?.cognome)
  const performanceConWo = performanceTecnici?.filter(t => t.wo_completati > 0) ?? []

  const chartData = andamento?.dati?.map((d: { mese: number; work_orders: number; completati: number; fatturato: number }) => ({
    ...d, mese: MESI[d.mese - 1],
  })) ?? []
  const storicoProdFiltered = storicoProd
    ? storicoProd.filter(r => r.completati > 0 || r.produzione_eur > 0)
    : []
  const storicoProdReversed = [...storicoProdFiltered].reverse()

  if (isError) return (
    <div style={{ textAlign: 'center', padding: 60 }}>
      <p style={{ color: '#ef4444', fontSize: 15, marginBottom: 12 }}>Impossibile caricare i dati. Verifica la connessione al server.</p>
      <button onClick={() => refetch()} className="btn-secondary">Riprova</button>
    </div>
  )
  if (isLoading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 240, gap: 12 }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%', border: '2.5px solid #e5e7eb', borderTopColor: '#1E40AF', animation: 'spin 0.7s linear infinite' }} />
      <span style={{ fontSize: 13, color: '#9ca3af' }}>Caricamento…</span>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  )

  const fmtDelta = (v: number | null | undefined): string | undefined => {
    if (v == null) return undefined
    return `${v > 0 ? '↑' : '↓'} ${Math.abs(v)}% vs mese prec.`
  }

  const mainCards = !isTecnico ? [
    { title: '€ Produzione Mese', value: `€ ${(kpi?.produzione_mese ?? 0).toLocaleString('it-IT', { maximumFractionDigits: 0 })}`, icon: TrendingUp, accent: '#8b5cf6', to: '/fatturazione', subtitle: fmtDelta(kpi?.delta_produzione) ?? 'vs mese precedente' },
    { title: 'Giorni con Chiusure', value: kpi?.produttivita?.giorni_attivi ?? 0, icon: CalendarDays, accent: '#dc2626', subtitle: 'giorni del mese con WO chiusi' },
    { title: 'Tecnici Operativi', value: kpi?.produttivita?.tecnici_operativi ?? 0, icon: Users, accent: '#1E40AF', to: '/report', subtitle: kpi?.produttivita?.wo_per_tecnico ? `${kpi.produttivita.wo_per_tecnico} WO/tecnico` : undefined },
  ] : [
    { title: 'WO Assegnati', value: mioTecnico?.wo_assegnati ?? 0, icon: ClipboardList, accent: '#1E40AF', to: '/mio-turno' },
    { title: 'WO Completati', value: mioTecnico?.wo_completati ?? 0, icon: CheckCircle2, accent: '#10b981', subtitle: 'Nel mese corrente' },
    { title: '% Completamento', value: `${mioTecnico?.tasso_completamento ?? 0}%`, icon: BarChart2, accent: '#F97316' },
    { title: 'Da Validare', value: kpi?.rapportini.da_approvare ?? 0, icon: FileText, accent: '#8b5cf6', to: '/rapportini' },
  ]

  const chartCardStyle = { background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '1.25rem 1.25rem 0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }
  const tableBoxStyle = { background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' as const, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }
  const thS: React.CSSProperties = { padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9ca3af', borderBottom: '1px solid #f3f4f6', background: '#fafafa', whiteSpace: 'nowrap' }
  const tdS: React.CSSProperties = { padding: '8px 12px', fontSize: 12, color: '#374151', borderBottom: '1px solid #f9fafb', whiteSpace: 'nowrap' }
  const monoS: React.CSSProperties = { fontFamily: "'JetBrains Mono', monospace" }
  const emptyRow = (cols: number, msg = 'Caricamento…') => (
    <tr><td colSpan={cols} style={{ ...tdS, textAlign: 'center', color: '#9ca3af', padding: '24px' }}>{msg}</td></tr>
  )

  const materialiRows = isTecnico
    ? (miaMateriali ? [miaMateriali] : [])
    : (materialiTecnici?.filter(m => !!m.targa) ?? [])

  return (
    <div className="space-y-8">

      {/* Header */}
      <div className="animate-fade-up flex items-end justify-between">
        <div>
          <h1 className="page-title">Dashboard Operativa</h1>
          <p className="page-subtitle">
            Panoramica in tempo reale — {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isTecnico && (
            <a href="/dashboard/stampa-a3" target="_blank" rel="noopener noreferrer" className="btn-secondary" title="Apre una vista stampabile in A3 orizzontale">
              <Printer size={15} /> Stampa A3
            </a>
          )}
          <button
            className="btn-secondary"
            onClick={inviaScreenshotWhatsapp}
            disabled={sendingScreenshot}
            title={`Invia screenshot delle prime ${KPI_SCREENSHOT_TILE_COUNT} tessere via WhatsApp`}
          >
            <MessageCircle size={15} /> {sendingScreenshot ? 'Invio...' : 'Screenshot WhatsApp'}
          </button>
          <Link to="/pratiche" className="btn-primary hidden sm:inline-flex">
            <ClipboardList size={15} /> Tutti i WO
          </Link>
        </div>
      </div>

      <div ref={kpiScreenshotRef}>
        {/* KPI cards */}
        <div className={`grid grid-cols-2 ${isTecnico ? 'lg:grid-cols-4' : 'lg:grid-cols-4'} gap-3 stagger`}>
          {!isTecnico && kpi?.dal_inizio && (
            <DualKpiCard
              accent="#dc2626"
              animDelay={0}
              items={[
                { title: 'Giorni Lavorati', value: kpi.dal_inizio.giorni_lavorati, icon: CalendarDays, color: '#dc2626', subtitle: 'giorni con WO completati fino ad oggi' },
                { title: 'Totale Prodotto', value: `€ ${kpi.dal_inizio.produzione_totale.toLocaleString('it-IT', { maximumFractionDigits: 0 })}`, icon: TrendingUp, color: '#8b5cf6', subtitle: 'produzione SAL totale' },
              ]}
            />
          )}
          {mainCards.map((card, i) => <KpiCard key={card.title} {...card} animDelay={(i + (!isTecnico ? 1 : 0)) * 60} />)}
        </div>

        {/* Operational cards — staff */}
        {!isTecnico && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 stagger" style={{ marginTop: '0.75rem' }}>
            <KpiCard title="Interventi Oggi" value={kpi?.work_orders.interventi_oggi ?? 0} icon={Clock} accent="#F97316" to="/pratiche" subtitle={`${kpi?.work_orders.completati_oggi ?? 0} completati oggi`} animDelay={0} />
            <KpiCard title="Completati / Mese" value={kpi?.work_orders.completati_questo_mese ?? 0} icon={CheckCircle2} accent="#10b981" subtitle={fmtDelta(kpi?.work_orders.delta_completati_mese)} animDelay={60} />
            <KpiCard title="WO Aperti" value={kpi?.work_orders.aperti ?? 0} icon={ClipboardList} accent="#1E40AF" to="/pratiche" animDelay={120} />
            <MediaRisorsaCard meseCorrente={importoMedioGiornoRisorsa} storico={storicoProdFiltered} animDelay={180} />
            <StackedKpiCard
              title="Prev. Fatturato Mese"
              icon={Receipt}
              accent="#10b981"
              animDelay={240}
              rows={[
                { title: 'Eseguito', value: `€ ${(kpi?.work_orders.previsione_fatturato_eseguito ?? 0).toLocaleString('it-IT', { maximumFractionDigits: 0 })}` },
                { title: 'Previsto', value: `€ ${(kpi?.work_orders.previsione_fatturato_assegnato ?? 0).toLocaleString('it-IT', { maximumFractionDigits: 0 })}` },
                { title: 'Totale', value: `€ ${(kpi?.work_orders.previsione_fatturato_mese ?? 0).toLocaleString('it-IT', { maximumFractionDigits: 0 })}`, isTotale: true },
              ]}
            />
            <KpiCard title="Da Validare" value={kpi?.rapportini.da_approvare ?? 0} icon={FileText} accent="#8b5cf6" to="/rapportini?stato=inviato" animDelay={300} />
          </div>
        )}
      </div>

      {/* Trasferimenti tecnico */}
      {isTecnico && trasfCount > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard title="Kit a Magazzino" value={kitMagazzinoCount} icon={PackageCheck} accent="#F97316" to="/magazzino" subtitle="Kit disponibili in centrale" />
        </div>
      )}

      {/* Produttività strip */}
      {kpi?.produttivita && (
        <div className="animate-fade-up" style={{ animationDelay: '0.25s' }}>
          <SectionLabel>Produttività — {new Date().toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}</SectionLabel>
          <div style={{
            background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 14,
            padding: '1rem 1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            display: 'grid',
            gridTemplateColumns: isMobile
              ? 'repeat(2, 1fr)'
              : (!isTecnico ? 'repeat(4, 1fr)' : 'repeat(4, 1fr)'),
            gap: '1px', overflow: 'hidden',
          }}>
            {([
              ...(isTecnico ? [{ label: 'Giorni con Chiusure', value: kpi.produttivita.giorni_attivi, sub: 'giorni con WO completati', icon: CalendarDays, color: '#6b7280' }] : []),
              { label: 'Tecnici Operativi',   value: kpi.produttivita.tecnici_operativi, sub: 'con WO completati', icon: Users, color: '#8b5cf6' },
              { label: 'WO / Giorno Lav.',    value: kpi.produttivita.wo_per_giorno.toFixed(1), sub: 'completati per giorno', icon: Zap, color: '#F97316' },
              { label: 'WO / Tecnico',        value: kpi.produttivita.wo_per_tecnico.toFixed(1), sub: 'completati per tecnico', icon: BarChart2, color: '#1E40AF' },
              ...(!isTecnico ? [{ label: '€ Produzione', value: `€ ${(kpi.produzione_mese / 1000).toFixed(1)}k`, sub: 'fatturato SAL mese', icon: TrendingUp, color: '#8b5cf6' }] : []),
            ] as { label: string; value: string | number; sub: string; icon: React.ElementType; color: string }[]).map((item, i) => (
              <div key={item.label} style={{ padding: '0.875rem 1rem', borderLeft: i > 0 ? '1px solid #f3f4f6' : 'none', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0, background: item.color + '14', color: item.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <item.icon size={16} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: 2 }}>{item.label}</p>
                  <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.4rem', fontWeight: 700, color: item.color, lineHeight: 1 }}>{item.value}</p>
                  <p style={{ fontSize: 10, color: '#9ca3af', marginTop: 3 }}>{item.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Storico produzione + Performance tecnici — staff only */}
      {!isTecnico && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

          {/* Storico 12 mesi */}
          <div style={tableBoxStyle}>
            <div style={{ padding: '1rem 1.25rem 0.75rem' }}>
              <SectionLabel>Storico Produzione — Ultimi 12 Mesi</SectionLabel>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thS}>Mese</th>
                    <th style={{ ...thS, textAlign: 'right' }}>€ Produzione</th>
                    <th style={{ ...thS, textAlign: 'right' }}>WO Compl.</th>
                    <th style={{ ...thS, textAlign: 'right' }}>Tecnici</th>
                    <th style={{ ...thS, textAlign: 'right' }}>Giorni Op.</th>
                    <th style={{ ...thS, textAlign: 'right' }}>WO/Tec</th>
                  </tr>
                </thead>
                <tbody>
                  {!storicoProd
                    ? emptyRow(6)
                    : storicoProdReversed.length === 0
                    ? emptyRow(6, 'Nessun dato')
                    : storicoProdReversed.map((row, i) => {
                        const isCurrent = i === 0
                        const base: React.CSSProperties = { ...tdS, background: isCurrent ? '#eff6ff' : undefined }
                        return (
                          <tr key={`${row.anno}-${row.mese}`}>
                            <td style={{ ...base, fontWeight: isCurrent ? 600 : 400, color: isCurrent ? '#1E40AF' : '#374151' }}>
                              {MESI[row.mese - 1]} {row.anno}
                            </td>
                            <td style={{ ...base, textAlign: 'right', ...monoS }}>
                              € {row.produzione_eur.toLocaleString('it-IT', { maximumFractionDigits: 0 })}
                            </td>
                            <td style={{ ...base, textAlign: 'right' }}>{row.completati}</td>
                            <td style={{ ...base, textAlign: 'right' }}>{row.n_tecnici}</td>
                            <td style={{ ...base, textAlign: 'right' }}>{row.giorni_operativi}</td>
                            <td style={{ ...base, textAlign: 'right' }}><WoTecnicoBadge value={row.wo_per_tecnico} /></td>
                          </tr>
                        )
                      })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Performance tecnici mese */}
          <div style={tableBoxStyle}>
            <div style={{ padding: '1rem 1.25rem 0.75rem' }}>
              <SectionLabel>Performance Tecnici — {MESI[new Date().getMonth()]} {new Date().getFullYear()}</SectionLabel>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thS}>Tecnico</th>
                    <th style={{ ...thS, textAlign: 'right' }}>WO</th>
                    <th style={{ ...thS, textAlign: 'right' }}>% Compl.</th>
                    <th style={{ ...thS, textAlign: 'right' }}>WO/Gg</th>
                    <th style={{ ...thS, textAlign: 'right' }}>€ Prod.</th>
                    <th style={{ ...thS, textAlign: 'center' }}>Perf.</th>
                  </tr>
                </thead>
                <tbody>
                  {!performanceTecnici
                    ? emptyRow(6)
                    : performanceConWo.length === 0
                    ? emptyRow(6, 'Nessun dato')
                    : performanceConWo.map((t, i) => {
                        const woPerGiorno = kpi?.produttivita?.giorni_attivi
                          ? (t.wo_completati / kpi.produttivita.giorni_attivi).toFixed(1)
                          : '—'
                        return (
                          <tr key={t.tecnico_id}>
                            <td style={tdS}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <TecnicoAvatar nome={t.nome} cognome={t.cognome} colorIdx={i} />
                                <span style={{ fontWeight: 500 }}>{t.nome} {t.cognome}</span>
                              </div>
                            </td>
                            <td style={{ ...tdS, textAlign: 'right', ...monoS }}>
                              {t.wo_completati}<span style={{ color: '#9ca3af' }}>/{t.wo_assegnati}</span>
                            </td>
                            <td style={{ ...tdS, textAlign: 'right' }}>{t.tasso_completamento}%</td>
                            <td style={{ ...tdS, textAlign: 'right', ...monoS }}>{woPerGiorno}</td>
                            <td style={{ ...tdS, textAlign: 'right', ...monoS }}>
                              {t.produzione_eur != null ? `€ ${t.produzione_eur.toLocaleString('it-IT', { maximumFractionDigits: 0 })}` : '—'}
                            </td>
                            <td style={{ ...tdS, textAlign: 'center' }}><PerfBadge tasso={t.tasso_completamento} /></td>
                          </tr>
                        )
                      })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Materiali tecnici — visibile solo se ci sono righe o dati in caricamento */}
      {(materialiRows.length > 0 || materialiTecnici === undefined) && <div style={tableBoxStyle}>
        <div style={{ padding: '1rem 1.25rem 0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <SectionLabel>Materiali Tecnici — {MESI[new Date().getMonth()]} {new Date().getFullYear()}</SectionLabel>
          <button onClick={() => refetchMateriali()} className="btn-ghost" style={{ fontSize: 12, padding: '2px 8px' }}>↻ Aggiorna</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thS}>Tecnico / Targa</th>
                <th style={{ ...thS, textAlign: 'right' }}>Kit Inst.</th>
                <th style={{ ...thS, textAlign: 'right' }}>Cavi (m)</th>
                <th style={{ ...thS, textAlign: 'right' }}>Mensole</th>
                <th style={{ ...thS, textAlign: 'right', borderLeft: '2px solid #dbeafe', color: '#1E40AF' }}>Kit Furg.</th>
                <th style={{ ...thS, textAlign: 'right' }}>Cavi Furg.</th>
                <th style={{ ...thS, textAlign: 'right' }}>Mensole Furg.</th>
                <th style={{ ...thS, textAlign: 'right' }}>Antenne Furg.</th>
                <th style={{ ...thS, textAlign: 'right', borderLeft: '2px solid #fde68a', color: '#92400e' }}>WO Assegnati</th>
                <th style={{ ...thS, textAlign: 'right', color: '#1E40AF' }}>Δ Kit</th>
                <th style={{ ...thS, textAlign: 'right', color: '#1E40AF' }}>Δ Antenne</th>
                <th style={{ ...thS, textAlign: 'center' }}>Scorta</th>
              </tr>
            </thead>
            <tbody>
              {materialiRows.length === 0
                ? emptyRow(12, materialiTecnici === undefined ? 'Caricamento…' : 'Nessun dato')
                : materialiRows.map((m, i) => {
                    const diffKitColor = m.diff_kit < 0 ? '#dc2626' : m.diff_kit === 0 ? '#f59e0b' : '#15803d'
                    const diffAntColor = m.diff_antenne < 0 ? '#dc2626' : m.diff_antenne === 0 ? '#f59e0b' : '#15803d'
                    return (
                    <tr key={m.tecnico_id}>
                      <td style={tdS}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <TecnicoAvatar nome={m.nome} cognome={m.cognome} colorIdx={i} />
                          <div>
                            <span style={{ fontWeight: 500 }}>{m.nome} {m.cognome}</span>
                            {m.targa && <span style={{ marginLeft: 6, fontSize: 10, color: '#9ca3af', ...monoS }}>{m.targa}</span>}
                          </div>
                        </div>
                      </td>
                      <td style={{ ...tdS, textAlign: 'right', ...monoS }}>{m.kit_installati}</td>
                      <td style={{ ...tdS, textAlign: 'right', ...monoS }}>{m.cavi_installati ?? '—'}</td>
                      <td style={{ ...tdS, textAlign: 'right', ...monoS }}>{m.mensole_installate ?? '—'}</td>
                      <td style={{ ...tdS, textAlign: 'right', ...monoS, borderLeft: '2px solid #dbeafe' }}>{m.kit_furgone}</td>
                      <td style={{ ...tdS, textAlign: 'right', ...monoS }}>{m.cavi_furgone}</td>
                      <td style={{ ...tdS, textAlign: 'right', ...monoS }}>{m.mensole_furgone}</td>
                      <td style={{ ...tdS, textAlign: 'right', ...monoS }}>{m.antenne_furgone}</td>
                      <td style={{ ...tdS, textAlign: 'right', ...monoS, borderLeft: '2px solid #fde68a', fontWeight: 600, color: '#92400e' }}>{m.pratiche_assegnate}</td>
                      <td style={{ ...tdS, textAlign: 'right', ...monoS, fontWeight: 700, color: diffKitColor }}>{m.diff_kit > 0 ? `+${m.diff_kit}` : m.diff_kit}</td>
                      <td style={{ ...tdS, textAlign: 'right', ...monoS, fontWeight: 700, color: diffAntColor }}>{m.diff_antenne > 0 ? `+${m.diff_antenne}` : m.diff_antenne}</td>
                      <td style={{ ...tdS, textAlign: 'center' }}><ScortaBadge scorta={m.scorta} /></td>
                    </tr>
                  )})}
            </tbody>
          </table>
        </div>
      </div>}

      {/* Tabella per regione */}
      {!isTecnico && (
        <div className="animate-fade-up" style={{ ...tableBoxStyle, animationDelay: '0.3s' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>Work Orders per Regione</span>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>— assegnati e ricevuti aperti</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thS}>Regione</th>
                  <th style={{ ...thS, textAlign: 'right' }}>Assegnati</th>
                  <th style={{ ...thS, textAlign: 'right' }}>Ricevuti</th>
                  <th style={{ ...thS, textAlign: 'right' }}>Totale</th>
                  <th style={{ ...thS, width: 120 }}>Distribuzione</th>
                </tr>
              </thead>
              <tbody>
                {!perRegione ? (
                  <tr><td colSpan={5} style={{ ...tdS, textAlign: 'center', color: '#9ca3af', padding: 24 }}>Caricamento…</td></tr>
                ) : perRegione.length === 0 ? (
                  <tr><td colSpan={5} style={{ ...tdS, textAlign: 'center', color: '#9ca3af', padding: 24 }}>Nessun WO aperto</td></tr>
                ) : perRegione.map(r => {
                  const maxTot = Math.max(...perRegione.map(x => x.totale), 1)
                  const pct = Math.round((r.totale / maxTot) * 100)
                  return (
                    <tr key={r.regione} style={{ borderBottom: '1px solid #f9fafb' }}>
                      <td style={{ ...tdS, fontWeight: 600, color: '#1e293b' }}>{r.regione}</td>
                      <td style={{ ...tdS, ...monoS, textAlign: 'right' }}>
                        <span style={{ background: '#dbeafe', color: '#1d4ed8', borderRadius: 6, padding: '2px 8px', fontWeight: 600 }}>{r.assegnato}</span>
                      </td>
                      <td style={{ ...tdS, ...monoS, textAlign: 'right' }}>
                        <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 6, padding: '2px 8px', fontWeight: 600 }}>{r.ricevuto}</span>
                      </td>
                      <td style={{ ...tdS, ...monoS, textAlign: 'right', fontWeight: 700 }}>{r.totale}</td>
                      <td style={{ ...tdS, paddingRight: 16 }}>
                        <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: '#1E40AF', borderRadius: 3 }} />
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Fatturato mensile — staff only */}
      {!isTecnico && chartData.length > 0 && (
        <div className="animate-fade-up" style={{ ...chartCardStyle, animationDelay: '0.4s' }}>
          <SectionLabel>Fatturato Mensile {new Date().getFullYear()}</SectionLabel>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="fGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#F97316" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#F97316" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis dataKey="mese" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={56}
                tickFormatter={v => `€${(v / 1000).toFixed(0)}k`} />
              <Tooltip content={<LightTooltip formatter={v => `€${v.toLocaleString('it-IT', { minimumFractionDigits: 2 })}`} />}
                cursor={{ stroke: 'rgba(249,115,22,0.15)', strokeWidth: 1 }} />
              <Area type="monotone" dataKey="fatturato" name="Fatturato" stroke="#F97316" strokeWidth={2}
                fill="url(#fGrad)" dot={{ fill: '#F97316', strokeWidth: 0, r: 3 }}
                activeDot={{ r: 5, fill: '#F97316', strokeWidth: 0 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}
